import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { Product } from '~/api/products/types';

import { useProducts } from '~/api/products';
import { AlertDialog, useTable } from '~/components';
import { ColumnIds } from '~/constants/table';
import { r, routes } from '~/providers/RouterProvider/router.routes';

import { ConfirmDeleteProductDialog } from './ConfirmDeleteProductDialog';
import { useProductsTableColumns } from './ProductsTable.hooks';

export const ProductsTable = () => {
  const { data: products } = useProducts();

  const data = useMemo(() => products.toSorted((product1, product2) => product2.id - product1.id), [products]);

  const deleteProductHandle = AlertDialog.useHandle<Product>();
  const dialogHandles = useMemo(() => ({ deleteProductHandle }), [deleteProductHandle]);

  const columns = useProductsTableColumns({ dialogHandles });
  const [table, Table] = useTable({ data, columns });

  const navigate = useNavigate();
  const { t } = useTranslation(['common']);

  return (
    <>
      <Table.Root table={table}>
        <Table.Menu>
          <Table.Search />

          <Table.Filters>
            <Table.Filter columnId={ColumnIds.productsStatus} type="multiselect" />
            <Table.Filter columnId={ColumnIds.productsActivityType} type="multiselect" />
            <Table.Filter columnId={ColumnIds.productsCategories} hideWhen="noOptions" type="multiselect" />
          </Table.Filters>
        </Table.Menu>

        <Table.Grid>
          <Table.Header />
          <Table.Body>
            {(row) => (
              <Table.Row
                actionLabel={t('common:actions.edit')}
                onClick={(product) => navigate(r(routes.editProduct, { productId: product.id }))}
                row={row}
              />
            )}
          </Table.Body>
        </Table.Grid>

        <Table.Pagination showPageSizeOptions />
      </Table.Root>

      <AlertDialog.Root handle={deleteProductHandle}>
        {({ payload: product }) => product && <ConfirmDeleteProductDialog onClose={() => deleteProductHandle.close()} product={product} />}
      </AlertDialog.Root>
    </>
  );
};
